import type { EventCategory } from '../types/event';

export interface CategoryMeta {
  id: EventCategory;
  label: string;
  color: string; // used for map markers and chips
  emoji: string;
}

// Order here drives the category multi-select order. Colors are Art Deco jewel
// and metallic tones so the chips and markers match the overall palette.
export const CATEGORIES: CategoryMeta[] = [
  { id: 'music', label: 'Music', color: '#c9a44d', emoji: '🎵' }, // brass
  { id: 'arts', label: 'Arts', color: '#b5683f', emoji: '🎨' }, // copper
  { id: 'market', label: 'Markets', color: '#3f9d7e', emoji: '🛍️' }, // emerald
  { id: 'theater', label: 'Theater', color: '#a8476a', emoji: '🎭' }, // garnet rose
  { id: 'sports', label: 'Sports', color: '#3a7ca5', emoji: '🏟️' }, // deco blue
  { id: 'family', label: 'Family', color: '#d9a23f', emoji: '👪' }, // amber
  { id: 'community', label: 'Community', color: '#4f9d8f', emoji: '🤝' }, // jade
  { id: 'other', label: 'Other', color: '#9a9b7a', emoji: '📌' }, // sage gold
];

const BY_ID = new Map(CATEGORIES.map((c) => [c.id, c]));

export function categoryMeta(id: EventCategory): CategoryMeta {
  return BY_ID.get(id) ?? CATEGORIES[CATEGORIES.length - 1];
}

// Arts and theater are one cultural cluster: selecting either shows both, which
// matches the data (Ticketmaster groups them as "Arts & Theatre") and avoids an
// empty result when the source has, say, theater but no standalone gallery art.
const CATEGORY_GROUPS: Partial<Record<EventCategory, EventCategory[]>> = {
  arts: ['arts', 'theater'],
  theater: ['arts', 'theater'],
};

export function expandCategories(cats: Iterable<EventCategory>): Set<EventCategory> {
  const input = [...cats];
  const out = new Set<EventCategory>(input);
  for (const c of input) {
    for (const g of CATEGORY_GROUPS[c] ?? []) out.add(g);
  }
  return out;
}
